import React, { useState } from 'react';

const BookmarksList = ({ bookmarks, categories, statuses, onEdit, onDelete, onOpenMultiple, selectedCategory }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === bookmarks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(bookmarks.map(b => b.bookmark_id)));
    }
  };

  const getStatusColor = (statusId) => {
    const status = statuses.find(s => s.status_id === statusId);
    return status?.color || '#3b82f6';
  };

  const getStatusName = (statusId) => {
    const status = statuses.find(s => s.status_id === statusId);
    return status?.name || statusId;
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.category_id === categoryId);
    return category?.name || 'Uncategorized';
  };

  return (
    <div className="p-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-foreground">
            {selectedCategory
              ? getCategoryName(selectedCategory)
              : 'All Bookmarks'}
          </h2>
          <span className="px-3 py-1 bg-secondary rounded-full text-sm text-secondary-foreground">
            {bookmarks.length}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={() => onOpenMultiple(Array.from(selectedIds))}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                data-testid="open-selected-button"
              >
                Open {selectedIds.size} tabs
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-4 py-2 border border-input rounded-lg hover:bg-accent/10"
              >
                Clear
              </button>
            </>
          )}

          <div className="flex border border-input rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-primary/10 text-primary' : ''}`}
              data-testid="view-grid"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${viewMode === 'list' ? 'bg-primary/10 text-primary' : ''}`}
              data-testid="view-list"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Bookmarks */}
      {bookmarks.length === 0 ? (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary mb-4">
            <svg className="w-10 h-10 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No bookmarks yet</h3>
          <p className="text-muted-foreground">Click "New" to add your first bookmark</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4' : 'space-y-3'}>
          {bookmarks.map(bookmark => (
            <div
              key={bookmark.bookmark_id}
              className={`bookmark-card border border-border rounded-lg p-4 bg-card hover:shadow-md ${selectedIds.has(bookmark.bookmark_id) ? 'ring-2 ring-primary' : ''}`}
              data-testid={`bookmark-${bookmark.bookmark_id}`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={selectedIds.has(bookmark.bookmark_id)}
                  onChange={() => toggleSelect(bookmark.bookmark_id)}
                  className="mt-1"
                  data-testid={`select-${bookmark.bookmark_id}`}
                />
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground truncate mb-1">
                    {bookmark.title}
                  </h3>
                  <a
                    href={bookmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline truncate block mb-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {bookmark.url}
                  </a>
                  
                  {bookmark.notes && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {bookmark.notes}
                    </p>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(bookmark.status) + '20',
                        color: getStatusColor(bookmark.status)
                      }}
                    >
                      {getStatusName(bookmark.status)}
                    </span>

                    {bookmark.tags && bookmark.tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-secondary text-secondary-foreground rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => window.open(bookmark.url, '_blank')}
                    className="p-2 hover:bg-accent/10 rounded"
                    title="Open"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onEdit(bookmark)}
                    className="p-2 hover:bg-accent/10 rounded"
                    title="Edit"
                    data-testid={`edit-${bookmark.bookmark_id}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDelete(bookmark.bookmark_id)}
                    className="p-2 hover:bg-destructive/10 rounded text-destructive"
                    title="Delete"
                    data-testid={`delete-${bookmark.bookmark_id}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookmarksList;
